import { Button, ProgressBar } from "@heroui/react";
import React, { type FC, use, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { DocumentsApi } from "@domain/api/DocumentsApi/DocumentsApi";
import { DmsType } from "@domain/models/dms";

import { FilesExisting } from "@dms/modules/DocumentUploader/components/FilesExisting/FilesExisting";
import { UploadFileList } from "./components/IsUploadingFiles/UploadFileList";
import { FilesSameHash } from "@dms/modules/DocumentUploader/components/FilesSameHash/FilesSameHash";
import { FilesInvalid } from "@dms/modules/DocumentUploader/components/FilesInvalid/FilesInvalid";
import { FileUploaderContext, FileUploaderControlContext } from "./context/FileUploaderContext";
import { hashCalculate } from "@domain/infrastructure/helpers/hashCalculate";
import { FileUploaderDropzone } from "@dms/modules/DocumentUploader/components/FileUploaderDropzone/FileUploaderDropzone";
import { type TUploadFile } from "@dms/types";
import { CIIDecoderFactory, GQL } from "@binale-tech/shared";
import { gql, type TypedDocumentNode } from "@apollo/client";
import { LoadingOverlay } from "@ui-components/LoadingOverlay";
import { useApolloClient } from "@apollo/client/react";
import { CompanyContext } from "@domain/context/CompanyContext";
import { base64StringToFile } from "@domain/converters/converters";
import { captureException } from "@sentry/react";

interface IProps {
    isDropzoneExpanded: boolean;
    onCollapseDropzone: () => void;
}

const invoiceConvertXRechnungXml: TypedDocumentNode<
    Pick<GQL.IMutation, "invoiceConvertXRechnungXml">,
    GQL.IMutationInvoiceConvertXRechnungXmlArgs
> = gql`
    mutation invoiceConvertXRechnungXml($input: InvoiceConvertXRechnungXmlInput!) {
        invoiceConvertXRechnungXml(input: $input) {
            pdf
        }
    }
`;

export const FileUploader: FC<IProps> = ({ isDropzoneExpanded, onCollapseDropzone }) => {
    const { companyGQL } = use(CompanyContext);
    const client = useApolloClient();
    const [progress, setProgress] = useState<number>(null);
    const [innerHeight, setInnerHeight] = useState(window.innerHeight);

    const { queueIsEmpty, useSingleDocumentMode, documentType } = use(FileUploaderContext);
    const { handleFilesDrop, setUnsupportedFiles, finish } = use(FileUploaderControlContext);

    useLayoutEffect(() => {
        const observer = new ResizeObserver(() => setInnerHeight(window.innerHeight));
        observer.observe(document.documentElement);
        return () => observer.disconnect();
    }, []);

    const height = useMemo((): number | undefined => {
        if (isDropzoneExpanded) {
            return innerHeight * 0.5;
        }
        return innerHeight * 0.25;
    }, [innerHeight, isDropzoneExpanded]);

    const handleChange = useCallback(
        async (files: File[]) => {
            onCollapseDropzone();
            setProgress(1);

            const addedFiles: Pick<TUploadFile, "file" | "hash">[] = [];

            for (const [index, file] of files.entries()) {
                setProgress((index / files.length) * 100);
                if (!["application/pdf", "application/xml", "text/xml"].includes(file.type)) {
                    setUnsupportedFiles((prev) => [...prev, { file, reason: "Unsupported file type" }]);
                    continue;
                }

                if (file.type === "application/pdf") {
                    const { isValid, error } = await DocumentsApi.checkPDF(file);
                    if (!isValid) {
                        setUnsupportedFiles((prev) => [...prev, { file, reason: "Invalid pdf: " + error.message }]);
                        captureException(error);
                        continue;
                    }

                    const hash = await hashCalculate(file);
                    addedFiles.push({ file, hash });
                } else if (["application/xml", "text/xml"].includes(file.type)) {
                    const rawXml = new TextDecoder().decode(await file.arrayBuffer());
                    const xmlProductKey = documentType?.type === DmsType.Deb ? GQL.IProductKey.Deb : GQL.IProductKey.Er;
                    const decoder = new CIIDecoderFactory(rawXml, xmlProductKey).getDecoder();
                    if (!decoder.getVersion().version.startsWith("urn:cen.eu:en16931:2017")) {
                        setUnsupportedFiles((prev) => [...prev, { file, reason: "Invalid E-Rechnung" }]);
                        continue;
                    }

                    const pdfBytes = await client
                        .mutate({
                            mutation: invoiceConvertXRechnungXml,
                            variables: {
                                input: {
                                    companyId: companyGQL.id,
                                    xml: rawXml,
                                },
                            },
                        })
                        .then((res) => res.data.invoiceConvertXRechnungXml.pdf)
                        .catch((err) => {
                            setUnsupportedFiles((prev) => [...prev, { file, reason: err.message }]);
                        });
                    if (pdfBytes) {
                        const filePdf = base64StringToFile(
                            pdfBytes,
                            file.name + ".pdf",
                            "application/pdf",
                            decoder.getInvoiceDate()?.date?.getTime(),
                        );
                        const hash = await hashCalculate(filePdf);

                        addedFiles.push({ file: filePdf, hash });
                    }
                }
            }

            handleFilesDrop(addedFiles);
            setProgress(100);
            await new Promise((r) => setTimeout(r, 500)).finally(() => setProgress(null));
        },
        [onCollapseDropzone, setUnsupportedFiles, client, companyGQL.id, handleFilesDrop, documentType?.type],
    );

    // console.log("FileUploader");

    return (
        <div className="relative">
            <LoadingOverlay isLoading={Number.isFinite(progress)} />
            <div className="flex flex-col gap-2 w-full">
                <div className="mb-5">
                    <FileUploaderDropzone
                        onChange={handleChange}
                        height={height}
                        useSingleDocumentMode={useSingleDocumentMode}
                    />
                </div>
                <div className="h-[25px]">
                    {Number.isFinite(progress) && (
                        <ProgressBar aria-label="Upload progress" value={Number(progress.toFixed(0))}>
                            <ProgressBar.Output />
                            <ProgressBar.Track>
                                <ProgressBar.Fill />
                            </ProgressBar.Track>
                        </ProgressBar>
                    )}
                </div>

                <FilesInvalid />
                <FilesSameHash />
                <FilesExisting />
                <UploadFileList />

                <div className="flex justify-end mt-4">
                    <Button
                        isDisabled={!queueIsEmpty}
                        onPress={finish}
                        data-testid="dms-file-uploader-submit"
                        aria-disabled={!queueIsEmpty}
                    >
                        <FormattedMessage id="app.button.done" />
                    </Button>
                </div>
            </div>
        </div>
    );
};
